import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Link as LinkIcon, Upload } from "lucide-react";

const MAX_FILE_BYTES = 1_500_000;

/**
 * Reusable image input — upload (file -> data URI) OR paste URL.
 * Renders preview, upload button, clear button, and URL field.
 */
export const ImageField = ({
  value = "",
  onChange,
  label = "Image",
  testIdPrefix = "image",
  aspect = "aspect-[4/5]",
  helpText = "Max 1.5 MB if uploading. PNG/JPG/WebP.",
}) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`Image must be under ${Math.round(MAX_FILE_BYTES / 1_000_000)} MB`);
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      onChange(dataUrl);
    } catch {
      toast.error("Could not read the file");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-field`}>
      {label && (
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#8D99AE] mb-2">{label}</div>
      )}
      <div className={`${aspect} rounded-2xl border border-[#E8E3DD] bg-[#FAF9F6] overflow-hidden flex items-center justify-center max-w-xs`}>
        {value ? (
          <img src={value} alt="Preview" className="w-full h-full object-cover" data-testid={`${testIdPrefix}-preview`} />
        ) : (
          <span className="text-xs text-[#8D99AE]">No image</span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2 max-w-xs">
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs bg-[#2C1E16] text-[#FAF9F6] hover:bg-[#3E2D24] disabled:opacity-60 rounded-full px-3 py-2 transition-colors"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={1.7} />}
          Upload
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testIdPrefix}-clear`}
            className="inline-flex items-center justify-center text-xs text-[#B33A3A] hover:bg-[#FAF9F6] rounded-full px-3 py-2"
          >
            Remove
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testIdPrefix}-file`}
          onChange={(e) => onPick(e.target.files?.[0])}
        />
      </div>
      <label className="mt-3 block max-w-xs">
        <span className="block text-[10px] uppercase tracking-[0.16em] text-[#8D99AE] mb-1 inline-flex items-center gap-1">
          <LinkIcon size={11} strokeWidth={1.7} /> Or paste an image URL
        </span>
        <input
          data-testid={`${testIdPrefix}-url`}
          value={value && value.startsWith("data:") ? "" : value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="admin-input text-sm"
        />
      </label>
      {helpText && <p className="mt-1 text-[10px] text-[#8D99AE] max-w-xs">{helpText}</p>}
    </div>
  );
};
